
declare var window: any;

import { Component, Vue, Prop } from 'vue-property-decorator';
import { etvService } from '../services/etv.service';
import { statisticService, eventType } from '../services/statistic.service';
import { Timer } from '../utils/timer';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-playlist';
import 'videojs-ima';

require('@silvermine/videojs-quality-selector')(videojs);
import registerPrivacyIcon from '../plugins/video-player/PrivacyIcon';

import { PlayerEvent } from '../interfaces/player-event.enum';

// overlays
import MetaOverlay from '@/components/MetaOverlay.vue';
import LogoOverlay from '@/components/LogoOverlay.vue'; // @ is an alias to /src

import { logService } from '../services/log.service';
import { adService } from '../services/ad.service';

import { PlayerConfig, EtvPlayer } from '@/interfaces/player-config.interface';
import { MetaInfo } from '@/interfaces/meta-info.interface';
import { Template } from '../App.vue';
import { iFrameService } from '../services/iframe.service';

// local config while runtime
const localConfig = require('../config/local.config.json');

@Component({
  components: {
    MetaOverlay,
    LogoOverlay,
  },
})
export default class Player extends Vue {
  @Prop() public playerConfig!: PlayerConfig;

  /**
   * Extended video.js Player
   */
  private player!: EtvPlayer;

  /**
   * video.js does not provide better type for options in version 8
   * @see [Video.js Options Reference](https://videojs.com/guides/options/)
   */
  private playerOptions!: any;

  public isLoading: boolean = false;

  public logoVisible: boolean = true;
  public metaVisible: boolean = false;
  public metaInfo: MetaInfo = {};

  private liveTimeout: any;
  private pauseTimer: any;
  private clockTimer: any;

  // status
  private isInitialPlay: boolean = true;
  private isAdComplete: boolean = false;
  private isAdPending: boolean = false;
  private midrollTimer: any;

  // statistic stuff
  private statisticInit: boolean = false;
  private statisticLifecyclePlay: boolean = false;
  private timeUpdateSend = -1;
  private retryOnError = 0;

  private created() {
    const { accountId, autoplay } = this.playerConfig;
    let defaultConfig = localConfig['default'] ?? {};
    let accountConfig = localConfig[accountId] ?? {};

    this.isLoading = autoplay;

    registerPrivacyIcon({ accountConfig, defaultConfig });

    this.playerOptions = {
      // videojs options
      muted: this.playerConfig.mute,
      language: this.playerConfig.language,
      aspectRatio: '16:9',
      autoplay: this.playerConfig.autoplay,
      fluid: true,
      // playbackRates: [0.7, 1.0, 1.5, 2.0],
      poster: this.playerConfig.previewImage,
      controls: true,
      controlBar: {
        children: ['playToggle', 'progressControl', 'volumePanel', 'privacyIcon', 'qualitySelector', 'fullscreenToggle'],
      },
    };
  }

  private mounted() {
    this.player = videojs('videoPlayer', this.playerOptions, () => {
      Vue.prototype.$player = this.player; // make global

      this.setSource();
      this.playerReady();

      // add html overlay to videojs container
      const logoOverlayRef: any = this.$refs.logoOverlay;
      if (logoOverlayRef && logoOverlayRef.$el) {
        this.player.el().appendChild(logoOverlayRef.$el);
      }

      const metaOverlayRef: any = this.$refs.metaOverlay;
      if (metaOverlayRef && metaOverlayRef.$el) {
        this.player.el().appendChild(metaOverlayRef.$el);
      }
    });

    if (this.playerConfig.advertisement.enabled) {
      logService.info('INIT ADS');
      const options = {
        id: 'videoPlayer',
        numRedirects: 7,
        disableFlashAds: true,
        adTagUrl: adService.getPrerollUrl(this.playerConfig),
      };
      this.player.ima(options);
    }

    this.bindPlayerEvents();
    this.bindImaEvents();
  }

  /**
   * Set live stream or playlist source
   */
  private setSource() {
    if (this.playerConfig.live) {
      this.player.src([
        {
          type: 'application/x-mpegURL',
          src: this.playerConfig.stream!,
        },
      ]);
    } else {
      if (this.playerConfig.playlist && this.playerConfig.playlist.length) {
        // @ts-ignore
        this.player.playlist(this.playerConfig.playlist);
        // Play through the playlist automatically.
        // @ts-ignore
        this.player.playlist.autoadvance(0);
      } else {
        this.$emit('templateChange', Template.PLAYER_ERROR);
      }
    }
  }

  private bindPlayerEvents() {
    // player events
    this.player.on(PlayerEvent.play, this.playerOnPlay);
    this.player.on(PlayerEvent.playing, this.playerOnPlaying);
    this.player.on(PlayerEvent.pause, this.playerOnPause);
    this.player.on(PlayerEvent.ended, this.playerOnEnded);
    this.player.on(PlayerEvent.error, this.playerOnError);
    this.player.on(PlayerEvent.timeupdate, this.playerOnTimeupdate);
    this.player.on(PlayerEvent.adserror, this.playerOnAdsError);
  }

  private bindImaEvents() {
    // IMA events
    this.player.on('adsready', () => {
      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.LOADED, () => {
        logService.trace('>>> ad LOADED');
        this.metaVisible = false;
        this.logoVisible = false;
        this.isAdPending = true;

        if (this.midrollTimer) {
          this.midrollTimer.pause();
        }
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.STARTED, (adEvent) => {
        this.isLoading = false;
        logService.trace('>>> ad STARTED');

        if (this.isPreroll()) {
          statisticService.ga('PrerollAdX Start');
        } else {
          statisticService.ga('PrerollAdX Start Ad Start');
        }
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, () => {
        this.isAdComplete = true;
        logService.trace('>>> ad COMPLETE');
        statisticService.ga('Ad endet');
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
        logService.trace('>>> ad ALL_ADS_COMPLETED');

        if (this.isPreroll()) {
          statisticService.ga('PrerollAdX Completed');
        } else {
          statisticService.ga('PrerollAdX Start Ad Completed');
        }

        this.isAdPending = false;

        this.player.play();
        if (this.midrollTimer) {
          this.midrollTimer.run();
        }
        this.logoVisible = true;
      });
    });
  }

  private isPreroll() {
    if (this.player.ima.controller && this.player.ima.controller.settings.adTagUrl.includes('preroll')) {
      return true;
    }
    return false;
  }

  private beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  }

  private playerReady(): void {
    logService.trace('[PLAYER] READY');
    statisticService.ga('Init');

    this.player.tech().on('retryplaylist', async () => {
      this.retryOnError++;

      if (this.retryOnError > 0 && this.retryOnError < 2) {
        logService.warn('retryOnError');
        await etvService.reload();
        this.player.pause();
        this.player.src(this.playerConfig.stream!);
        this.player.load();
        this.player.play();
      }

      if (this.retryOnError >= 2) {
        this.retryOnError = 0;
        logService.error('[PLAYER] retryplaylist error');
        this.$emit('templateChange', Template.PLAYER_ERROR);
      }
    });
  }

  public handleClickEvents() {
    if (this.player.paused()) {
      logService.trace('[PLAYER][CLICK] pause');

      if (this.isAdComplete && this.playerConfig) {
        this.pauseTimer = setTimeout(() => {
          logService.info('[PLAYER] handlePauseTimeout ' + Number(process.env.VUE_APP_AUTO_STOP_TIMER_ON_PAUSE) / 1000 + 's');
          this.$emit('templateChange', Template.STOP);
        }, Number(process.env.VUE_APP_AUTO_STOP_TIMER_ON_PAUSE));
      }

      if (this.liveTimeout) {
        logService.info('[PLAYER] reset handleStreamTimeout');
        clearTimeout(this.liveTimeout);
      }
      statisticService.ga('Video Pause');
      statisticService.report('pause', this.playerConfig.stream);
    } else {
      logService.trace('[PLAYER][CLICK] play');
      statisticService.ga('Video Start');
      this.handleStreamTimeout();
    }
  }

  private playerOnPlay() {
    logService.info('[PLAYER] onPlay' + (this.isInitialPlay ? ' (initial)' : ''));
    iFrameService.sendStateChangeEvent(this.playerConfig, this.player);

    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
    }

    if (!this.statisticLifecyclePlay) {
      statisticService.ga('Lifecycle Play');
      this.handleStreamTimeout();
      this.statisticLifecyclePlay = true;
    }

    if (this.isInitialPlay && !this.isAdComplete && this.playerConfig.advertisement.enabled) {
      this.isLoading = true;
    } else {
      this.isLoading = false;
    }

    if (this.isInitialPlay) {
      this.isInitialPlay = false;
      statisticService.ga('Video Initial Play');
      statisticService.report('play', this.playerConfig.stream);
      this.handleMidroll();
    } else {
      statisticService.ga('Video Play');
      statisticService.report('play', this.playerConfig.stream);

      if (this.midrollTimer) {
        this.midrollTimer.run();
      }
    }
  }

  private playerOnPause() {
    logService.info('[PLAYER] onPause');
    iFrameService.sendStateChangeEvent(this.playerConfig, this.player);
    if (this.midrollTimer) {
      this.midrollTimer.pause();
    }
  }

  private playerOnPlaying() {
    logService.info('[PLAYER] onPlaying');
    this.handleMetaOverlay();
  }

  private playerOnTimeupdate() {
    const currentTime: any = this.player.currentTime();
    const time = parseInt(currentTime, 10);
    if (time % 30 === 0 && time !== this.timeUpdateSend) {
      this.timeUpdateSend = time;
      statisticService.report('change', this.playerConfig.stream);
      statisticService.ga('Position Change ' + time + 's');
    }
  }

  /**
   * create new pauseable timer to trigger midrolls
   */
  private handleMidroll() {
    if (this.playerConfig.advertisement && this.playerConfig.advertisement.midroll > 0) {
      const playMidrollInMs = this.playerConfig.advertisement.midroll * 1000;
      this.midrollTimer = new Timer(this.startMidroll, playMidrollInMs);
    }
  }

  /**
   * https://github.com/googleads/videojs-ima/blob/main/examples/advanced/ads.js
   */
  private startMidroll() {
    if (!this.isAdPending) {
      logService.trace('[IMA] start midroll');
      this.isAdPending = true;
      this.player.ima.setContentWithAdTag(null, adService.getMidrollUrl(this.playerConfig), true);
      this.player.ima.initializeAdDisplayContainer();
      this.player.ima.requestAds();
    }
  }

  private playerOnEnded() {
    logService.info('[PLAYER] onEnded');

    this.metaVisible = false;
    this.isInitialPlay = true;

    statisticService.ga('Video Completed');
    statisticService.report('finish', this.playerConfig.stream);

    if (this.midrollTimer) {
      this.midrollTimer.pause();
    }
  }

  private async playerOnError(event: any) {
    logService.error('[PLAYER] onError', event);
    this.$emit('templateChange', Template.PLAYER_ERROR);
  }

  private async playerOnAdsError(event: any) {
    logService.warn(`[PLAYER] adsError: [${event.data.AdError.h}] ${event.data.AdError.o} - ${event.data.AdError.l}`);
    this.isAdComplete = true;
    this.isLoading = false;
    this.isAdPending = false;
    this.player.play();
  }

  private handleStreamTimeout() {
    if (this.playerConfig.stopLiveStreamInSec) {
      logService.info('[PLAYER] set handleStreamTimeout ' + Number(this.playerConfig.stopLiveStreamInSec / 60).toFixed(2) + ' min');

      if (this.liveTimeout) {
        clearTimeout(this.liveTimeout);
      }
      this.liveTimeout = setTimeout(() => {
        if (this.playerConfig.stopLiveStreamInSec) {
          logService.info('[PLAYER] handleStreamTimeout ' + Number(this.playerConfig.stopLiveStreamInSec / 60).toFixed(2) + ' min');
        }
        if (this.player.isFullscreen()) {
          this.player.exitFullscreen();
        }

        statisticService.report('stop', this.playerConfig.stream);

        this.$emit('templateChange', Template.STOP);
      }, this.playerConfig.stopLiveStreamInSec * 1000);
    }
  }

  private handleMetaOverlay() {
    // https://earthtv.atlassian.net/browse/WV-126
    if (!this.playerConfig.overlay) return false;

    // set location from playlist clip
    let clipDate = new Date();
    if (!this.playerConfig.live && this.playerConfig.playlist) {
      // @ts-ignore
      const index = this.player.playlist.currentIndex();
      // @ts-ignore
      /* tslint:disable */
      if (this.playerConfig.playlist[index]['location']) {
        this.playerConfig.location = this.playerConfig.playlist[index]['location'];
      }
      if (this.playerConfig.playlist[index]['time']) {
        clipDate = this.playerConfig.playlist[index]['time'];
      }
      /* tslint:enable */
    }

    if (this.playerConfig.location) {
      const lang = this.playerConfig.language;

      this.metaInfo = {
        country: this.playerConfig.location.country[lang],
        city: this.playerConfig.location.city[lang],
        date: clipDate,
        timeZone: this.playerConfig.location.timeZone,
      };

      // display live time
      if (this.playerConfig.live) {
        clearInterval(this.clockTimer);
        this.clockTimer = setInterval(() => {
          this.metaInfo.date = new Date();
        }, 5000);
      }

      this.metaVisible = true;
      logService.info('[PLAYER] handleMetaOverlay', this.metaInfo);
    }
  }
}
